/**
 * Resource source — what made a row.
 *
 * The platform has always recorded WHO created a resource (`user_id`, `created_by`,
 * `audit_log.user_id`) and never WHAT. Those are orthogonal, and only the first was
 * captured, so a connector the AI orchestrator composed is byte-identical in the
 * database to one a person built by hand: the agent forwards the caller's `x-jwt-*`
 * headers verbatim (services/agent/src/lib/api-client.ts), and an external MCP client
 * authenticates with an `fsk_` key like any other API caller.
 *
 * ── The rule ────────────────────────────────────────────────────────────────────
 *
 *   `source` is whatever made the HTTP call that WROTE the row.
 *
 * It is derived once, at the edge, from the auth path the gateway already resolved
 * for authorization, and never re-derived downstream. That single rule settles every
 * composite case without a precedence table:
 *
 *   person in the dashboard ..................................... 'user'
 *   person asks the agent, the agent writes ..................... 'agent'
 *   Claude over MCP writes ....................................... 'mcp'
 *   Claude over MCP asks the agent, the agent writes ............. 'agent'
 *   the agent proposes, a person clicks Apply ................... 'user'
 *   a background sweep rewrites a row ........................... 'system'
 *
 * The "agent proposed, a person approved" case resolves to 'user' deliberately:
 * accountability follows the last party that could have said no.
 *
 * ── Trust ───────────────────────────────────────────────────────────────────────
 *
 * `source` is a LABEL, never a permission. Nothing in the platform may branch an
 * authorization decision on it. That matters because one value — 'mcp' — cannot be
 * proven server-side: an MCP host and a partner's cron script present the same
 * `fsk_` key, so the only available signal is the client declaring itself. A caller
 * who lies has mislabelled their own data and gained nothing. The day something
 * authorizes on `source`, that stops being true.
 */
/**
 * Every value `source` may hold. Ordered most- to least-specific about agency, which
 * is also the order the badges read best in a filter.
 *
 * Deliberately NOT a Postgres CHECK constraint. `connector_change_event.source_kind`
 * shipped with a CHECK listing three values while the code branched on five, so two
 * could never be stored and three branches sat unreachable until migration
 * 1787084568007 widened it — a change that also had to rewrite live rows in its
 * `down()`. A seventh source is inevitable; it must not need a constraint migration.
 */
export const RESOURCE_SOURCES = [
    /** A person acting in the dashboard, on a verified Keycloak session. */
    "user",
    /** The embedded widget or a hosted setup link — an `emb_` token, i.e. an end user. */
    "embed",
    /** A partner's own code holding an `fsk_` API key. */
    "api",
    /** An external MCP client (Claude Code, claude.ai, any MCP host) on an `fsk_` key. */
    "mcp",
    /** fastn's own AI orchestrator or one of its sub-agents. */
    "agent",
    /** No human request at all: scheduler, sweep, import, backfill, migration. */
    "system",
];
const SOURCE_SET = new Set(RESOURCE_SOURCES);
/** True when `value` is one of the known sources. */
export function isResourceSource(value) {
    return typeof value === "string" && SOURCE_SET.has(value);
}
/**
 * Narrow an untrusted string to a ResourceSource, or `null`.
 *
 * Used on the read side of the gateway-minted header and when hydrating a row
 * written by an older or newer deploy. An unrecognised value becomes `null`
 * ("unknown"), never a guess — the whole point of this field is that it is
 * accurate, and a wrong source is worse than an absent one.
 */
export function parseResourceSource(value) {
    return isResourceSource(value) ? value : null;
}
/**
 * The header the gateway mints and every upstream reads.
 *
 * `x-gateway-` is load-bearing, not cosmetic: services/api/src/server.ts sweeps
 * inbound headers by PREFIX FAMILY (`x-jwt-`, `x-apikey-`, `x-gateway-`), and
 * tests/unit/api/inbound-header-strip.test.ts fails any internal header not named
 * into one of those families. A name outside them would be client-spoofable.
 */
export const SOURCE_HEADER = "x-gateway-source";
/**
 * The caller-declared client hint. Deliberately OUTSIDE the stripped families,
 * because unlike the header above this one is the caller's to set — it is how an
 * MCP host says it is an MCP host. Read only through {@link deriveSource}, which
 * treats it as a label and bounds it.
 */
export const CLIENT_HINT_HEADER = "x-fastn-client";
/** Longest client hint we will look at. Bounds an attacker-sized header. */
const MAX_CLIENT_HINT_LEN = 64;
/**
 * Derive the source for a request. Pure, total, and the single definition — the same
 * reason `uclWorkspace` (ucl-workspace.ts) and `resolveCredOwnerOrg` (tenant-owner.ts)
 * each live in exactly one module: a second copy drifts silently and nothing fails.
 *
 * Order matters. The agent declaration is checked FIRST because it is the only one
 * that describes the writer rather than the door: fastn's agent reaches the gateway
 * carrying the human's own `x-jwt-*` headers (it forwards them verbatim), so on
 * authPath alone an agent write is indistinguishable from the dashboard session that
 * asked for it. That indistinguishability is the exact defect this field exists to
 * close.
 */
export function deriveSource(input) {
    if (input.agentVerified === true)
        return "agent";
    switch (input.authPath) {
        case "jwt":
            return "user";
        case "embed":
            return "embed";
        case "api_key":
            return isMcpClientHint(input.clientHint) ? "mcp" : "api";
        case "service":
            return "system";
    }
}
/**
 * Classify the auth path from the `sub` the gateway stamped.
 *
 * Reading the resolved `x-jwt-sub` rather than re-inspecting the credential is what
 * keeps this from drifting: the gateway already encodes the path in the prefix
 * (`apikey:` at server.ts:2210, `embed:` at :3697 and :3782, bare Keycloak sub
 * otherwise), so a new auth branch that follows that convention is classified
 * correctly on the day it lands, with no second list to update.
 *
 * `null`/empty means no authenticated principal — a service-token channel or
 * background work — which is `'service'`.
 */
export function authPathFromSub(sub) {
    if (typeof sub !== "string" || sub.length === 0)
        return "service";
    if (sub.startsWith("apikey:"))
        return "api_key";
    if (sub.startsWith("embed:"))
        return "embed";
    return "jwt";
}
/**
 * True when a caller declared itself an MCP host.
 *
 * Matches `mcp` exactly or an `mcp/<something>` prefix, so a host may version itself
 * (`mcp/claude-code`) without this needing to know the names. Case-insensitive and
 * length-bounded: the value is caller-controlled.
 */
function isMcpClientHint(value) {
    if (typeof value !== "string")
        return false;
    const hint = value.trim().slice(0, MAX_CLIENT_HINT_LEN).toLowerCase();
    return hint === "mcp" || hint.startsWith("mcp/");
}
/**
 * Human-facing label for a source. One definition so the dashboard, the embed and
 * the admin console cannot disagree about what to call these.
 */
export const RESOURCE_SOURCE_LABELS = {
    user: "User",
    embed: "Embed",
    api: "API",
    mcp: "MCP",
    agent: "AI Agent",
    system: "System",
};
/** What a null/unrecognised source renders as. Never a guess. */
export const UNKNOWN_SOURCE_LABEL = "Unknown";
//# sourceMappingURL=resource-source.js.map